import { SOCKET_EVENT } from "@sendsar/protocol";
import type { SendsarClient } from "./client.js";

export type TenantPresenceListener = (onlineUserIds: ReadonlySet<string>) => void;

/**
 * Track tenant-wide online users from `tenant-presence` / `tenant-presence-snapshot` events.
 * Presence is set automatically when the WebSocket connects with a session JWT.
 */
export function createTenantPresenceTracker(client: SendsarClient) {
  let onlineUserIds = new Set<string>();
  const listeners = new Set<TenantPresenceListener>();

  function notify() {
    const snapshot = onlineUserIds;
    for (const listener of listeners) {
      listener(snapshot);
    }
  }

  const onSnapshot = ({ onlineUserIds: ids }: { onlineUserIds: string[] }) => {
    onlineUserIds = new Set(ids);
    notify();
  };
  const onPresence = ({ userId, online }: { userId: string; online: boolean }) => {
    const next = new Set(onlineUserIds);
    if (online) next.add(userId);
    else next.delete(userId);
    onlineUserIds = next;
    notify();
  };

  const offSnapshot = client.on(SOCKET_EVENT.TENANT_PRESENCE_SNAPSHOT, onSnapshot);
  const offPresence = client.on(SOCKET_EVENT.TENANT_PRESENCE, onPresence);

  return {
    getOnlineUserIds: () => onlineUserIds,
    subscribe(listener: TenantPresenceListener) {
      listeners.add(listener);
      listener(onlineUserIds);
      return () => listeners.delete(listener);
    },
    destroy() {
      offSnapshot();
      offPresence();
      listeners.clear();
    },
  };
}
